import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Save, CheckCircle, AlertCircle, Printer, Send, ChevronDown, Trash2, Layers, RefreshCw, FileText, Plus, Minus, FileCheck, ClipboardList, Check } from 'lucide-react';
import { format, isValid, parseISO } from 'date-fns';
import AppHeader from '../components/AppHeader';

// Safe date formatting helper to prevent white screen crashes
const safeFormatDate = (dateValue, formatStr, fallback = '-') => {
  if (!dateValue) return fallback;
  try {
    const date = typeof dateValue === 'string' ? parseISO(dateValue) : new Date(dateValue);
    if (!isValid(date)) return fallback;
    return format(date, formatStr);
  } catch (e) {
    console.warn('Date formatting error:', e, dateValue);
    return fallback;
  }
};

const enconOrange = '#ee7203';
const enconLightGrey = '#e5e7eb';

// Adhoc Adjustment Modal
const AdhocModal = ({ isOpen, onClose, onSave, productName, currentQty, currentNote }) => {
  const [mode, setMode] = useState(null); // null, 'add', 'minus'
  const [quantity, setQuantity] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (isOpen) {
      if (currentQty && currentQty !== 0) {
        setMode(currentQty > 0 ? 'add' : 'minus');
        setQuantity(Math.abs(currentQty).toString());
        setNote(currentNote || '');
      } else {
        setMode(null);
        setQuantity('');
        setNote('');
      }
    }
  }, [isOpen, currentQty, currentNote]);

  if (!isOpen) return null;

  const handleSave = () => {
    const qty = parseFloat(quantity);
    if (!qty || isNaN(qty) || qty <= 0) return;
    const finalQty = mode === 'minus' ? -qty : qty;
    onSave(finalQty, note);
    onClose();
  };

  const handleClear = () => {
    onSave(0, '');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4"
        style={{ border: `3px solid ${enconOrange}` }}
        data-testid="adhoc-modal"
      >
        <div className="p-5">
          <h3 className="text-lg font-bold text-gray-900 mb-1">Adhoc Adjustment</h3>
          <p className="text-sm text-gray-500 mb-4 truncate" title={productName}>{productName}</p>

          {mode === null ? (
            <div>
              <p className="text-sm text-gray-600 mb-4">Would you like to add or deduct stock?</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setMode('add')}
                  className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-white transition-all hover:opacity-90"
                  style={{ backgroundColor: '#22c55e' }}
                  data-testid="adhoc-add-btn"
                >
                  <Plus className="w-5 h-5" /> Add
                </button>
                <button
                  onClick={() => setMode('minus')}
                  className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-white transition-all hover:opacity-90"
                  style={{ backgroundColor: '#ef4444' }}
                  data-testid="adhoc-minus-btn"
                >
                  <Minus className="w-5 h-5" /> Deduct
                </button>
              </div>
            </div>
          ) : (
            <div>
              <div className="flex gap-2 mb-3">
                <button
                  onClick={() => setMode('add')}
                  className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all ${mode === 'add' ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-500'}`}
                >
                  + Add
                </button>
                <button
                  onClick={() => setMode('minus')}
                  className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all ${mode === 'minus' ? 'bg-red-500 text-white' : 'bg-gray-100 text-gray-500'}`}
                >
                  - Deduct
                </button>
              </div>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder={mode === 'add' ? 'Add quantity' : 'Minus quantity'}
                className="mb-3 text-center text-lg font-semibold h-12"
                style={{ borderColor: enconOrange }}
                autoFocus
                data-testid="adhoc-quantity-input"
              />
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Add a note (reason for adjustment)..."
                className="mb-4 text-sm"
                rows={2}
                style={{ borderColor: enconOrange }}
                data-testid="adhoc-note-input"
              />
              <div className="flex justify-between">
                {currentQty && currentQty !== 0 ? (
                  <button onClick={handleClear} className="text-sm text-red-500 hover:text-red-700 font-medium">Remove Adhoc</button>
                ) : <span />}
                <Button
                  onClick={handleSave}
                  disabled={!quantity || parseFloat(quantity) <= 0}
                  style={{ backgroundColor: enconOrange, color: 'white' }}
                  data-testid="adhoc-save-btn"
                >
                  Save
                </Button>
              </div>
            </div>
          )}

          <button
            onClick={onClose}
            className="mt-3 w-full text-center text-sm text-gray-400 hover:text-gray-600"
            data-testid="adhoc-cancel-btn"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const PICountDetail = ({ user }) => {
  const { countId } = useParams();
  const navigate = useNavigate();
  
  const [count, setCount] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDiscrepancyModal, setShowDiscrepancyModal] = useState(false);
  const [discrepancies, setDiscrepancies] = useState([]);
  const [selectedForRecount, setSelectedForRecount] = useState({}); // Track which items are selected for recount
  const [adHocNotes, setAdHocNotes] = useState({}); // Track notes for ad hoc items
  const [showSendPrintDropdown, setShowSendPrintDropdown] = useState(false);
  const [sendingToWarehouse, setSendingToWarehouse] = useState(false);
  const [creatingDiscrepancyReport, setCreatingDiscrepancyReport] = useState(false);
  const [creatingSecondCount, setCreatingSecondCount] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Selective-delete tick state — mirrors the modal's checkbox tree.
  const [deleteSelections, setDeleteSelections] = useState({});
  const [refreshingM4, setRefreshingM4] = useState(false);
  const [adhocModalOpen, setAdhocModalOpen] = useState(null); // product id or null
  // Selection state: map of productId → true/false. Auto-ticked for any
  // row with a non-zero discrepancy. User can toggle. Used by the new
  // top action bar (Recount / Schedule / Adjustments).
  const [selected, setSelected] = useState({});
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleDate, setScheduleDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().slice(0,10);
  });
  const [scheduling, setScheduling] = useState(false);
  const [runningRecount, setRunningRecount] = useState(false);
  const [runningAdjustments, setRunningAdjustments] = useState(false);
  const [completing, setCompleting] = useState(false);
  // Controls the "Next recount — Count first or Send to W/H?" modal.
  const [recountActionOpen, setRecountActionOpen] = useState(false);
  const [showOriginal, setShowOriginal] = useState(false);   // off by default on recounts

  // Assignee selection modal — shown before any "Send to W/H" action so the
  // office can specify who should complete the count in the warehouse.
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assigneePendingId, setAssigneePendingId] = useState(null); // countId to send once assignee chosen
  const [whAssigneeName, setWhAssigneeName] = useState('');
  const [whAssigneeEmail, setWhAssigneeEmail] = useState('');
  const [whTeamMembers, setWhTeamMembers] = useState({});
  const [whTeamFilter, setWhTeamFilter] = useState('default');
  // Linked counts in the same chain (original + recounts). Used to render
  // the pill navigation above the header so users can hop between linked counts.
  const [linkedCounts, setLinkedCounts] = useState([]);
  // Related artifacts spawned from this chain — displayed as Adjusted /
  // Rescheduled jump buttons on the right of the pill nav.
  const [relatedAdjustments, setRelatedAdjustments] = useState([]);
  const [relatedBuckets, setRelatedBuckets] = useState([]);
  
  // Combined count tabs state
  const [activeSupplierTab, setActiveSupplierTab] = useState(null);
  const [supplierGroups, setSupplierGroups] = useState([]);
  const [isCombinedCount, setIsCombinedCount] = useState(false);
  
  useEffect(() => {
    loadCountDetails();
  }, [countId]);
  
  const loadCountDetails = async () => {
    try {
      setLoading(true);
      
      // Get count details
      const countRes = await api.get(`${API}/pi-counts/${countId}`);
      const countData = countRes.data.count;
      setCount(countData);
      setShowOriginal(!!countData?.show_original);
      
      // Check if this is a combined count
      const combined = countData?.is_combined || false;
      setIsCombinedCount(combined);
      
      if (combined && countData?.supplier_groups) {
        setSupplierGroups(countData.supplier_groups);
        // Set first supplier group as active tab
        if (countData.supplier_groups.length > 0 && !activeSupplierTab) {
          setActiveSupplierTab(countData.supplier_groups[0].supplier_group_id);
        }
      }
      
      // Get products for this count's supplier group with stock data
      const productsRes = await api.get(`${API}/pi-counts/${countId}/products`);
      setProducts(productsRes.data.products || []);
      // If the products response includes updated supplier_groups (with accurate totals), use those
      if (productsRes.data.supplier_groups && productsRes.data.supplier_groups.length > 0) {
        setSupplierGroups(productsRes.data.supplier_groups);
      }

      // Fetch linked counts in the same chain so we can render pill navigation.
      try {
        const linkedRes = await api.get(`${API}/pi-counts/${countId}/linked`);
        setLinkedCounts(linkedRes.data.linked || []);
      } catch (err) {
        console.warn('Failed to load linked counts', err);
        setLinkedCounts([]);
      }

      // Fetch related artifacts (adjustment reports + scheduled buckets)
      // produced by this count's chain so the pill row can show Adjusted /
      // Rescheduled jump buttons on the right.
      try {
        const relRes = await api.get(`${API}/pi-counts/${countId}/related`);
        setRelatedAdjustments(relRes.data.adjustments || []);
        setRelatedBuckets(relRes.data.schedule_buckets || []);
      } catch (err) {
        console.warn('Failed to load related artifacts', err);
        setRelatedAdjustments([]);
        setRelatedBuckets([]);
      }
      
    } catch (error) {
      console.error('Error loading count details:', error);
      toast.error('Failed to load count details');
    } finally {
      setLoading(false);
    }
  };
  
  // Filter products by active supplier group tab
  const filteredProducts = isCombinedCount && activeSupplierTab
    ? products.filter(p => p.supplier_group_id === activeSupplierTab)
    : products;
  
  // Calculate progress for each supplier group in combined counts
  const getSupplierGroupProgress = (supplierGroupId) => {
    const groupProducts = products.filter(p => p.supplier_group_id === supplierGroupId);
    const counted = groupProducts.filter(p => p.physical_count !== null && p.physical_count !== undefined).length;
    return { counted, total: groupProducts.length };
  };
  
  // Original on-hand for a row (the figure the parent count was measured
  // against), or null when Show Original is off / not a recount / the row has
  // no original figure. effectiveOnHand is the figure the discrepancy is
  // measured against right now.
  const originalOnHandFor = (p) => {
    if (!showOriginal || !count?.is_second_count) return null;
    if (p.original_on_hand === null || p.original_on_hand === undefined) return null;
    const n = parseFloat(p.original_on_hand);
    return Number.isNaN(n) ? null : n;
  };
  const effectiveOnHand = (p) => {
    const orig = originalOnHandFor(p);
    return orig !== null ? orig : p.on_hand_balance;
  };

  // Show Original toggle: save any typed edits, persist the choice on this
  // count (backend recalculates every counted row's discrepancy), then reload
  // the rows so On Hand Qty and Discrepancy switch together.
  const handleToggleShowOriginal = async () => {
    const next = !showOriginal;
    try {
      await saveProgress({ silent: true });
      await api.put(`${API}/pi-counts/${countId}/show-original`, { show_original: next });
      const productsRes = await api.get(`${API}/pi-counts/${countId}/products?_=${Date.now()}`);
      setProducts(productsRes.data.products || []);
      setCount(prev => (prev ? { ...prev, show_original: next } : prev));
      setShowOriginal(next);
    } catch (error) {
      console.error('Error switching on-hand view:', error);
      toast.error(error?.response?.data?.detail || 'Failed to switch on-hand view');
    }
  };

  const handlePhysicalCountChange = (productId, productCode, value) => {
    setProducts(prev => prev.map(p => {
      const isMatch = productId ? p.id === productId : (p.code || p.product_code) === productCode;
      if (isMatch) {
        const physical = value === '' ? 0 : parseFloat(value) || 0;
        return { 
          ...p, 
          physical_count: value === '' ? null : physical,
          discrepancy: calculateDiscrepancy(effectiveOnHand(p), physical, p.on_the_road || 0, p.adhoc_quantity || 0)
        };
      }
      return p;
    }));
    markDirty();
  };
  
  const handleOnTheRoadChange = (productId, productCode, value) => {
    setProducts(prev => prev.map(p => {
      const isMatch = productId ? p.id === productId : (p.code || p.product_code) === productCode;
      if (isMatch) {
        const onTheRoad = value === '' ? 0 : parseFloat(value) || 0;
        return {
          ...p,
          on_the_road: onTheRoad,
          discrepancy: calculateDiscrepancy(effectiveOnHand(p), p.physical_count || 0, onTheRoad, p.adhoc_quantity || 0)
        };
      }
      return p;
    }));
    markDirty();
  };

  // Shelf Life counts only: records the expiry/use-by date found on the
  // pack for this product line. Editable at any point, from either the
  // office count detail page or the warehouse mobile count view.
  const handleExpiryDateChange = (productId, productCode, value) => {
    setProducts(prev => prev.map(p => {
      const isMatch = productId ? p.id === productId : (p.code || p.product_code) === productCode;
      if (isMatch) {
        return { ...p, expiry_date: value || null };
      }
      return p;
    }));
    markDirty();
  };
  
  const calculateDiscrepancy = (onHandQty, physical, onTheRoad, adhoc = 0) => {
    return (physical + onTheRoad + adhoc) - onHandQty;
  };

  const handleAdhocSave = (productId, productCode, qty, note) => {
    setProducts(prev => prev.map(p => {
      const isMatch = productId ? p.id === productId : (p.code || p.product_code) === productCode;
      if (isMatch) {
        const adhocQty = qty || 0;
        const physicalBase = p.physical_count || 0;
        return {
          ...p,
          adhoc_quantity: adhocQty,
          adhoc_note: note || '',
          discrepancy: calculateDiscrepancy(effectiveOnHand(p), physicalBase + adhocQty, p.on_the_road || 0)
        };
      }
      return p;
    }));
    setAdhocModalOpen(null);
    markDirty();
  };
  
  const saveProgress = async ({ silent = false } = {}) => {
    try {
      setSaving(true);
      await api.put(`${API}/pi-counts/${countId}/products`, {
        products: products.map(p => ({
          id: p.id,
          product_id: p.id,
          // Products from GET use `product_code`. Earlier this was sent
          // as `p.code` (undefined) so the backend's merge stored every
          // edit under an empty product_code, silently losing the data
          // on the next reload. Always send the resolved code.
          product_code: p.product_code || p.code,
          // Preserve null for physical_count when the row hasn't been
          // counted yet. Coercing null → 0 makes the backend store 0
          // which then looks "counted" (`physical_count !== null`),
          // poisoning the progress bar.
          physical_count: (p.physical_count === null || p.physical_count === undefined) ? null : p.physical_count,
          on_the_road: p.on_the_road || 0,
          discrepancy: p.discrepancy || 0,
          adhoc_quantity: p.adhoc_quantity || 0,
          adhoc_note: p.adhoc_note || '',
          expiry_date: p.expiry_date || null,
          supplier_group_id: p.supplier_group_id,
          supplier_group_name: p.supplier_group_name
        }))
      });
      if (!silent) toast.success('Progress saved');
    } catch (error) {
      console.error('Error saving progress:', error);
      if (!silent) toast.error('Failed to save progress');
    } finally {
      setSaving(false);
    }
  };

  // ── AUTO-SAVE ────────────────────────────────────────────────────────
  // Any edit to a physical count / on-the-road / adhoc row calls
  // `markDirty()`. A debounced effect then fires a silent save 800ms after
  // the user stops typing. A visible "Auto-saved" indicator is rendered
  // next to the manual Save icon in the action bar so users can see the
  // state at a glance.
  const dirtyRef = useRef(false);
  const autoSaveTimer = useRef(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved'

  const markDirty = () => {
    dirtyRef.current = true;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(async () => {
      if (!dirtyRef.current) return;
      dirtyRef.current = false;
      setAutoSaveStatus('saving');
      try {
        // Use saveProgressRef.current so we always call the latest version of
        // saveProgress (which captures the latest `products` state). Without
        // this, fast typing causes a stale closure to be invoked — e.g. typing
        // "10" quickly stores the products snapshot from when "1" was typed,
        // and the save sends on_the_road=1 instead of 10.
        await saveProgressRef.current({ silent: true });
        setAutoSaveStatus('saved');
        setTimeout(() => setAutoSaveStatus(prev => prev === 'saved' ? 'idle' : prev), 1800);
      } catch (e) {
        setAutoSaveStatus('idle');
      }
    }, 800);
  };

  // Flush any pending auto-save when the user navigates away so typing that
  // just happened isn't lost. We use a ref to always pick up the LATEST
  // saveProgress (otherwise the cleanup captures the first-render closure
  // where `products` is still the initial empty state and the save sends
  // nothing useful).
  const saveProgressRef = useRef(saveProgress);
  saveProgressRef.current = saveProgress;
  useEffect(() => {
    return () => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
      if (dirtyRef.current) {
        saveProgressRef.current({ silent: true });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh M4 On Hand Data
  const refreshM4Data = async () => {
    try {
      setRefreshingM4(true);
      const response = await api.post(`${API}/pi-counts/${countId}/refresh-m4-data`);
      toast.success(response.data.message || 'M4 data refreshed successfully');

      // Cache-busting: tack a `?_=ts` onto the GETs so any layer in front
      // of us (CDN, browser, axios) is forced to re-fetch the freshly
      // updated docs. Without this, deployed users sometimes saw stale
      // on_hand values until they reloaded the whole page.
      const ts = Date.now();
      const countRes = await api.get(`${API}/pi-counts/${countId}?_=${ts}`);
      const productsRes = await api.get(`${API}/pi-counts/${countId}/products?_=${ts}`);
      const freshProducts = productsRes.data.products || [];
      const freshCount = countRes.data.count || null;

      // Mirror what loadCountDetails does so combined-count tabs + supplier
      // group totals stay in sync on this codepath.
      setCount(freshCount);
      if (freshCount?.is_combined) {
        setIsCombinedCount(true);
        if (Array.isArray(freshCount.supplier_groups)) setSupplierGroups(freshCount.supplier_groups);
      }
      setProducts(freshProducts);

      // Re-derive the recount/discrepancy list from the fresh products so
      // the recount panel doesn't keep showing the OLD on_hand it captured
      // when "Run Count" was originally clicked. Match by product code,
      // keep typed physical/on-the-road/adhoc state, recompute discrepancy.
      setDiscrepancies(prev => {
        if (!Array.isArray(prev) || prev.length === 0) return prev;
        const byCode = new Map();
        freshProducts.forEach(p => {
          const c = (p.code || p.product_code || '').toUpperCase();
          if (c) byCode.set(c, p);
        });
        return prev.map(item => {
          const c = (item.code || item.product_code || '').toUpperCase();
          const fresh = byCode.get(c);
          if (!fresh) return item;
          const newOnHand = fresh.on_hand_quantity ?? fresh.on_hand_balance ?? fresh.free_stock ?? 0;
          const physical = item.physical_count || 0;
          const onTheRoad = item.on_the_road || 0;
          const adhoc = item.adhoc_quantity || 0;
          return {
            ...item,
            on_hand_quantity: newOnHand,
            on_hand_balance: newOnHand,
            free_stock: fresh.free_stock ?? newOnHand,
            stock_unit: fresh.stock_unit ?? item.stock_unit,
            description: fresh.description ?? item.description,
            product_name: fresh.product_name ?? item.product_name,
            discrepancy: (physical + onTheRoad + adhoc) - newOnHand,
          };
        });
      });
    } catch (error) {
      console.error('Error refreshing M4 data:', error);
      toast.error(error.response?.data?.detail || 'Failed to refresh M4 data');
    } finally {
      setRefreshingM4(false);
    }
  };
  
  // Auto-tick every row that currently has a non-zero discrepancy. Keeps
  // the user's explicit toggles intact by only seeding rows not yet known
  // in the `selected` map. Re-runs whenever products change.
  // Key falls back to product_code so legacy docs that lost `id` during
  // save still auto-select correctly.
  const productKey = (p) => p?.id || p?.product_code || p?.code || null;

  useEffect(() => {
    setSelected(prev => {
      const next = { ...prev };
      let dirty = false;
      (products || []).forEach(p => {
        const key = productKey(p);
        if (key == null) return;
        if (next[key] !== undefined) return;
        // Auto-tick rule: only seed rows where the physical count has been
        // entered AND the absolute discrepancy is at least 1. A count that
        // matches exactly (disc = 0) or lands on a fractional difference
        // under 1 shouldn't be auto-selected — user requested this so exact
        // counts don't get swept into recount/adjustment bulk actions.
        const hasDisc = p.physical_count !== null
          && p.physical_count !== undefined
          && Math.abs(p.discrepancy || 0) >= 1;
        if (hasDisc) { next[key] = true; dirty = true; }
      });
      return dirty ? next : prev;
    });
  }, [products]);

  const toggleSelected = (productId) => setSelected(prev => ({ ...prev, [productId]: !prev[productId] }));
  const selectedProducts = () => (products || []).filter(p => selected[productKey(p)]);
  const selectedCodes = () => selectedProducts().map(p => (p.code || p.product_code || '').toUpperCase()).filter(Boolean);

  const currentRecountNumber = count?.recount_number || (count?.is_second_count ? 1 : 0);
  const nextRecountLabel = (currentRecountNumber + 2 === 2) ? '2nd Count'
    : (currentRecountNumber + 2 === 3) ? '3rd Count'
    : `${currentRecountNumber + 2}th Count`;

  // Top-action-bar: create the next linked count for the selected items.
  // `mode` = 'count-first' (navigate to the new count so the user can start
  // counting) or 'send-to-wh' (create the recount and immediately push it
  // to the warehouse queue without opening it).
  const handleStartNextRecount = async (mode = 'count-first') => {
    const codes = selectedCodes();
    if (!codes.length) { toast.error('Tick at least one row first'); return; }
    setRunningRecount(true);
    try {
      await saveProgress();
      const res = await api.post(`${API}/pi-counts/${countId}/create-second-count`, {
        selected_item_codes: codes,
      });
      const newId = res?.data?.count?.id;
      if (mode === 'send-to-wh') {
        if (!newId) throw new Error('Recount id missing from response');
        // Close the recount-action chooser then open the assignee modal in
        // PLACE for the newly-created recount. Using the local modal avoids
        // a navigate→deep-link round-trip that was unreliable on some flows
        // (the assignee modal sometimes failed to appear, allowing recounts
        // to be sent to the warehouse without an assignee).
        setRecountActionOpen(false);
        openAssignModal(newId);
      } else {
        toast.success(`${nextRecountLabel} created for ${codes.length} item${codes.length === 1 ? '' : 's'}`);
        navigate(newId ? `/pi-counts/${newId}` : '/pi-counts');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Failed to create recount');
    } finally { setRunningRecount(false); setRecountActionOpen(false); }
  };

  const handleScheduleSubmit = async () => {
    const codes = selectedCodes();
    if (!codes.length) { toast.error('Tick at least one row first'); return; }
    if (!scheduleDate) { toast.error('Pick a date'); return; }
    setScheduling(true);
    try {
      // saveProgress() has its own try/catch that never re-throws. We still
      // await it so physical counts are persisted before scheduling, but a
      // save failure must not block the schedule itself.
      try { await saveProgress(); } catch (_) {}
      await api.post(`${API}/pi-counts/${countId}/schedule-recount`, {
        selected_item_codes: codes,
        scheduled_date: scheduleDate,
      });
      toast.success(`Scheduled ${codes.length} item${codes.length === 1 ? '' : 's'} for ${scheduleDate}`);
      setScheduleOpen(false);
      navigate('/pi-counts');
    } catch (e) {
      // Surface as much detail as possible so we can debug scheduling failures.
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const msg = detail
        ? `Schedule failed: ${detail}${status ? ` (${status})` : ''}`
        : `Schedule failed: ${e?.message || 'unknown error'}`;
      console.error('Schedule error', { status, detail, error: e });
      toast.error(msg);
    } finally { setScheduling(false); }
  };

  // Complete this count (and close the whole linked chain). Propagates
  // to any sibling/parent count that is still in an in-flight state
  // so clicking Complete on the latest card shuts the chain down.
  const handleCompleteCount = async () => {
    if (completing) return;
    setCompleting(true);
    try {
      await saveProgress().catch(() => {});
      const res = await api.post(`${API}/pi-counts/${countId}/complete`);
      const n = res?.data?.completed_ids?.length || 0;
      toast.success(n > 1 ? `Marked ${n} linked counts as complete` : 'Count marked complete');
      navigate('/pi-counts');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to complete count');
    } finally { setCompleting(false); }
  };


  // Adjustments: route selected items into the existing adjustment flow via
  // create-adjustment-report (server already exists as
  // `/pi-counts/adjustment-reports/create`). Once the items have been
  // pushed to the adjustment report, mark the originating count as
  // completed — the user's intent on "Move to Adjustments" is "I'm done
  // with this count, the rest were on-target", so leaving the count in
  // an in-progress state forced an extra Complete click on the way out.
  const handleAdjustments = async () => {
    const sel = selectedProducts();
    if (!sel.length) { toast.error('Tick at least one row first'); return; }
    setRunningAdjustments(true);
    try {
      await saveProgress();
      await api.post(`${API}/pi-counts/adjustment-reports/create`, {
        count_id: countId,
        count_name: count?.supplier_name || 'Unknown',
        count_date: count?.date || new Date().toISOString().split('T')[0],
        count_type: 'adjustment',
        items: sel.map(d => ({
          product_code: d.code || d.product_code,
          product_name: d.name || d.description || d.product_name,
          expected_qty: originalOnHandFor(d) ?? d.on_hand_quantity ?? d.on_hand_balance ?? d.free_stock ?? 0,
          counted_qty: d.physical_count,
          discrepancy: d.discrepancy || 0,
          on_the_road: d.on_the_road || 0,
          is_ad_hoc: false,
          ad_hoc_note: ''
        })),
      });
      // Auto-complete the originating count (and propagate to the rest of
      // the linked chain via the same endpoint Complete uses) so the user
      // doesn't have to click Complete separately. Non-fatal — the
      // adjustment report is the primary outcome of this action.
      try {
        await api.post(`${API}/pi-counts/${countId}/complete`);
      } catch (e) {
        console.warn('Auto-complete after adjustment failed (non-fatal):', e?.response?.data || e);
      }
      toast.success(`Adjustment report created with ${sel.length} item${sel.length === 1 ? '' : 's'} · Count marked complete`);
      navigate('/pi-counts');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create adjustment report');
    } finally { setRunningAdjustments(false); }
  };

  const runCount = async () => {
    // Calculate all discrepancies (including adhoc adjustments)
    console.log('🔍 runCount called - products:', products.length);
    console.log('🔍 Products with physical counts:', products.filter(p => p.physical_count !== null && p.physical_count !== undefined).length);
    
    const discrepancyList = products
      .filter(p => p.physical_count !== null && p.physical_count !== undefined)
      .map(p => {
        const disc = calculateDiscrepancy(p.on_hand_balance, p.physical_count || 0, p.on_the_road || 0, p.adhoc_quantity || 0);
        console.log(`🔍 Product ${p.code}: onHand=${p.on_hand_balance}, physical=${p.physical_count}, adhoc=${p.adhoc_quantity}, discrepancy=${disc}`);
        return {
          ...p,
          discrepancy: disc
        };
      })
      .filter(p => {
        const absDisc = Math.abs(p.discrepancy);
        const passes = absDisc > 0.01;
        console.log(`🔍 Filter check for ${p.code}: abs(${p.discrepancy}) = ${absDisc}, passes filter (>0.01): ${passes}`);
        return passes;
      });
    
    console.log('🔍 Discrepancies found:', discrepancyList.length);
    discrepancyList.forEach(d => console.log(`  - ${d.code}: ${d.discrepancy}`));
    
    setDiscrepancies(discrepancyList);
    
    // Initialize all items as selected for recount by default
    const initialSelection = {};
    discrepancyList.forEach(item => {
      initialSelection[item.id] = true;
    });
    setSelectedForRecount(initialSelection);
    setAdHocNotes({});
    
    // DO NOT SAVE - Just show the modal
    // await saveProgress(); // REMOVED - clicking Run Count should not save data
    
    // Show discrepancy modal
    setShowDiscrepancyModal(true);
  };
  
  // Toggle selection for recount
  const toggleRecountSelection = (itemId) => {
    setSelectedForRecount(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };
  
  // Select/Deselect all items
  const toggleAllSelection = (selectAll) => {
    const newSelection = {};
    discrepancies.forEach(item => {
      newSelection[item.id] = selectAll;
    });
    setSelectedForRecount(newSelection);
  };
  
  // Update ad hoc note for an item
  const updateAdHocNote = (itemId, note) => {
    setAdHocNotes(prev => ({
      ...prev,
      [itemId]: note
    }));
  };
  
  // Get items selected for recount
  const getSelectedItems = () => discrepancies.filter(d => selectedForRecount[d.id]);
  
  // Get ad hoc items (not selected for recount)
  const getAdHocItems = () => discrepancies.filter(d => !selectedForRecount[d.id]);
  
  const completeCount = async () => {
    // Check if any ad-hoc items are missing notes
    const adHocItems = getAdHocItems();
    const missingNotes = adHocItems.filter(item => !adHocNotes[item.id]?.trim());
    
    if (missingNotes.length > 0) {
      toast.error(`Please add notes for all ad-hoc items (${missingNotes.length} remaining)`);
      return;
    }
    
    try {
      // Prepare discrepancy data with notes for ad-hoc items
      const allDiscrepancies = discrepancies.map(d => ({
        product_id: d.id,
        product_code: d.code,
        product_name: d.name,
        on_hand: d.on_hand_balance,
        physical_count: d.physical_count,
        on_the_road: d.on_the_road,
        discrepancy: d.discrepancy,
        is_ad_hoc: !selectedForRecount[d.id],
        ad_hoc_note: adHocNotes[d.id] || ''
      }));
      
      await api.post(`${API}/pi-counts/${countId}/complete`, {
        discrepancies: allDiscrepancies
      });
      
      // If there are discrepancies, create an adjustment report
      if (discrepancies.length > 0) {
        try {
          await api.post(`${API}/pi-counts/adjustment-reports/create`, {
            count_id: countId,
            count_name: count?.supplier_name || 'Unknown',
            count_date: count?.date || new Date().toISOString().split('T')[0],
            count_type: 'normal',
            items: discrepancies.map(d => ({
              product_code: d.code,
              product_name: d.name,
              expected_qty: d.on_hand_quantity ?? d.on_hand_balance ?? d.free_stock,
              counted_qty: d.physical_count,
              discrepancy: d.discrepancy,
              on_the_road: d.on_the_road || 0,
              is_ad_hoc: !selectedForRecount[d.id],
              ad_hoc_note: adHocNotes[d.id] || ''
            }))
          });
          toast.success('Count completed - Adjustment report created');
        } catch (adjError) {
          console.error('Error creating adjustment report:', adjError);
          toast.success('Count completed (adjustment report failed)');
        }
      } else {
        toast.success('Count completed successfully');
      }
      
      setShowDiscrepancyModal(false);
      navigate('/pi-counts');
    } catch (error) {
      console.error('Error completing count:', error);
      toast.error('Failed to complete count');
    }
  };
  
  // Create second count with only selected items
  const handleCreateSecondCountWithSelection = async () => {
    const selectedItems = getSelectedItems();
    
    if (selectedItems.length === 0) {
      toast.error('Please select at least one item for the second count');
      return;
    }
    
    // Check if any ad-hoc items are missing notes
    const adHocItems = getAdHocItems();
    const missingNotes = adHocItems.filter(item => !adHocNotes[item.id]?.trim());
    
    if (missingNotes.length > 0) {
      toast.error(`Please add notes for all ad-hoc items (${missingNotes.length} remaining)`);
      return;
    }
    
    setCreatingSecondCount(true);
    try {
      // Save ad-hoc items first
      if (adHocItems.length > 0) {
        await api.post(`${API}/pi-counts/${countId}/save-ad-hoc`, {
          items: adHocItems.map(d => ({
            product_id: d.id,
            product_code: d.code,
            product_name: d.name,
            on_hand: d.on_hand_balance,
            physical_count: d.physical_count,
            discrepancy: d.discrepancy,
            note: adHocNotes[d.id] || ''
          }))
        });
      }
      
      // Create second count with selected items only
      await api.post(`${API}/pi-counts/${countId}/create-second-count`, {
        selected_product_ids: selectedItems.map(item => item.id)
      });
      
      toast.success(`Second count created with ${selectedItems.length} item(s)${adHocItems.length > 0 ? ` - ${adHocItems.length} ad-hoc saved` : ''}`);
      setShowDiscrepancyModal(false);
      navigate('/pi-counts');
    } catch (error) {
      console.error('Error creating second count:', error);
      toast.error(error.response?.data?.detail || 'Failed to create second count');
    } finally {
      setCreatingSecondCount(false);
    }
  };
  
  // Create discrepancy report with only selected items
  const handleCreateDiscrepancyReportWithSelection = async () => {
    const selectedItems = getSelectedItems();
    
    if (selectedItems.length === 0) {
      toast.error('Please select at least one item for the discrepancy report');
      return;
    }
    
    // Check if any ad-hoc items are missing notes
    const adHocItems = getAdHocItems();
    const missingNotes = adHocItems.filter(item => !adHocNotes[item.id]?.trim());
    
    if (missingNotes.length > 0) {
      toast.error(`Please add notes for all ad-hoc items (${missingNotes.length} remaining)`);
      return;
    }
    
    setCreatingDiscrepancyReport(true);
    try {
      // Save ad-hoc items first
      if (adHocItems.length > 0) {
        await api.post(`${API}/pi-counts/${countId}/save-ad-hoc`, {
          items: adHocItems.map(d => ({
            product_id: d.id,
            product_code: d.code,
            product_name: d.name,
            on_hand: d.on_hand_balance,
            physical_count: d.physical_count,
            discrepancy: d.discrepancy,
            note: adHocNotes[d.id] || ''
          }))
        });
      }
      
      // Create discrepancy report with selected items only
      await api.post(`${API}/pi-counts/${countId}/create-discrepancy-report`, {
        selected_product_ids: selectedItems.map(item => item.id)
      });
      
      toast.success(`Discrepancy report created with ${selectedItems.length} item(s)${adHocItems.length > 0 ? ` - ${adHocItems.length} ad-hoc saved` : ''}`);
      setShowDiscrepancyModal(false);
      navigate('/pi-counts');
    } catch (error) {
      console.error('Error creating discrepancy report:', error);
      toast.error(error.response?.data?.detail || 'Failed to create discrepancy report');
    } finally {
      setCreatingDiscrepancyReport(false);
    }
  };
  
  const subHeaderLeft = (
    <>
      <button
        onClick={() => navigate('/pi-counts')}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold bg-white/90 hover:bg-white transition-colors"
        style={{ color: enconOrange }}
        data-testid="pi-count-detail-back-btn"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">Back</span>
      </button>
      <div className="flex items-center gap-1.5 rounded-full px-3 py-1 flex-shrink-0" style={{ background: 'rgba(0,0,0,0.22)' }}>
        <span className="text-white font-bold text-xs sm:text-sm whitespace-nowrap">PI Count</span>
      </div>
    </>
  );
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <AppHeader user={user} subHeaderLeft={subHeaderLeft} />
        <div className="flex items-center justify-center h-screen">
          <p className="text-lg text-gray-600">Loading count details...</p>
        </div>
      </div>
    );
  }
  
  if (!count) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <AppHeader user={user} subHeaderLeft={subHeaderLeft} />
        <div className="flex items-center justify-center h-screen">
          <p className="text-lg text-red-600">Count not found</p>
        </div>
      </div>
    );
  }
  
  const totalDiscrepancies = products.filter(p => 
    p.physical_count !== null && Math.abs(p.discrepancy || 0) > 0.01
  ).length;
  
  const completedCount = products.filter(p => p.physical_count !== null).length;

  // Print count sheet
  const handlePrint = () => {
    setShowSendPrintDropdown(false);
    
    // Create printable content
    const printWindow = window.open('', '_blank');
    
    // For combined counts, group products by supplier group and add page breaks
    let productsBySupplier = {};
    
    if (isCombinedCount && supplierGroups.length > 1) {
      // Group products by supplier
      supplierGroups.forEach(sg => {
        const sgProducts = products.filter(p => p.supplier_group_id === sg.supplier_group_id);
        productsBySupplier[sg.supplier_group_id] = {
          name: sg.supplier_name,
          products: sgProducts
        };
      });
    } else {
      // Single supplier - all products
      productsBySupplier['single'] = {
        name: count?.supplier_name || 'Stock Take Count Sheet',
        products: products
      };
    }
    
    // Generate HTML for each supplier group with page breaks
    const supplierSections = Object.entries(productsBySupplier).map(([key, data], idx) => `
      <div class="supplier-section ${idx > 0 ? 'page-break' : ''}">
        <div class="header">
          <h1>${data.name}</h1>
          <div class="date">${count?.date ? format(new Date(count.date), 'EEEE, dd MMMM yyyy') : ''}</div>
          ${isCombinedCount ? `<div class="combined-badge">Combined Count - Page ${idx + 1} of ${supplierGroups.length}</div>` : ''}
        </div>
        
        <div class="name-field">
          <label>Counted By:</label>
          <span class="line"></span>
        </div>
        
        <table>
          <thead>
            <tr>
              <th class="desc-cell">Product Description</th>
              <th class="unit-cell">Unit</th>
              <th class="count-cell">Count</th>
              <th class="notes-cell">Notes</th>
            </tr>
          </thead>
          <tbody>
            ${data.products.map(p => `
              <tr>
                <td class="desc-cell">${p.name || p.description || p.product_name || p.code || ''}${p.not_in_stock ? ' <span style="color:red;font-weight:bold;">*</span>' : ''}</td>
                <td class="unit-cell">${p.stock_unit || ''}</td>
                <td class="count-cell"><div class="count-box"></div></td>
                <td class="notes-cell"><div class="notes-box"></div></td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <div class="footer">
          ${data.products.length} Products | Generated ${format(new Date(), 'dd/MM/yyyy')}
        </div>
      </div>
    `).join('');
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Stock Take Count Sheet - ${count?.supplier_name}</title>
        <style>
          @page { size: A4 portrait; margin: 12mm; }
          body { font-family: Arial, sans-serif; font-size: 11px; }
          .supplier-section { padding-bottom: 20px; }
          .page-break { page-break-before: always; }
          .header { text-align: center; margin-bottom: 15px; }
          .header h1 { color: #ee7203; font-size: 22px; margin: 0 0 5px 0; }
          .header .date { font-size: 13px; color: #666; margin-bottom: 10px; }
          .header .combined-badge { 
            display: inline-block;
            background: #9333ea; 
            color: white; 
            padding: 4px 12px; 
            border-radius: 12px; 
            font-size: 11px; 
            font-weight: bold;
            margin-top: 5px;
          }
          .name-field { margin-bottom: 15px; }
          .name-field label { font-weight: bold; font-size: 12px; }
          .name-field .line { border-bottom: 1px solid #333; width: 250px; display: inline-block; margin-left: 10px; height: 18px; }
          table { width: 100%; border-collapse: collapse; margin-top: 8px; }
          th { background: #ee7203; color: white; padding: 8px 6px; text-align: left; font-size: 10px; }
          td { padding: 10px 6px; border-bottom: 1px solid #ccc; vertical-align: middle; }
          tr:nth-child(even) { background: #fafafa; }
          .desc-cell { width: 45%; }
          .unit-cell { width: 8%; text-align: center; }
          .count-cell { width: 18%; }
          .count-box { border: 1px solid #888; height: 28px; background: white; border-radius: 3px; }
          .notes-cell { width: 29%; }
          .notes-box { border: 1px solid #888; height: 28px; background: white; border-radius: 3px; }
          .footer { margin-top: 15px; text-align: center; font-size: 9px; color: #999; }
          @media print { 
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            tr { page-break-inside: avoid; }
            .page-break { page-break-before: always; }
          }
        </style>
      </head>
      <body>
        ${supplierSections}
      </body>
      </html>
    `;
    
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  };

  // ── Assignee-selection helpers ───────────────────────────────────────────
  const loadWhTeamMembers = async () => {
    try {
      const branchId = count?.branch_id || 'C30';
      const res = await api.get(`${API}/teams/members`, { params: { branch_id: branchId } });
      setWhTeamMembers(res.data.teams || {});
    } catch (_) {}
  };

  const getWhFilteredMembers = () => {
    const all = Object.entries(whTeamMembers).flatMap(([team, members]) =>
      (members || []).map(m => ({ ...m, team: team.toLowerCase().replace(/\s+/g, '_') }))
    );
    if (whTeamFilter === 'all') return all;
    if (whTeamFilter === 'warehouse') return all.filter(m => m.team === 'warehouse');
    if (whTeamFilter === 'operations') return all.filter(m => m.team === 'operations');
    if (whTeamFilter === 'office_sales') return all.filter(m => m.team === 'office_sales');
    if (whTeamFilter === 'admin') return all.filter(m => m.team === 'admin');
    // default: warehouse + operations
    return all.filter(m => ['warehouse', 'operations'].includes(m.team));
  };

  const openAssignModal = (countIdToSend) => {
    setAssigneePendingId(countIdToSend);
    setWhAssigneeName('');
    setWhAssigneeEmail('');
    setWhTeamFilter('default');
    setShowAssignModal(true);
    loadWhTeamMembers();
  };

  const confirmSendWithAssignee = async () => {
    if (!assigneePendingId) return;
    setShowAssignModal(false);
    setSendingToWarehouse(true);
    try {
      await api.put(`${API}/pi-counts/${assigneePendingId}/send-to-warehouse`, {
        assigned_to: whAssigneeName.trim(),
        assigned_email: whAssigneeEmail.trim(),
      });
      toast.success('Count sent to Warehouse');
      navigate('/pi-counts?open=warehouse');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send to warehouse');
    } finally {
      setSendingToWarehouse(false);
      setAssigneePendingId(null);
    }
  };
  // ─────────────────────────────────────────────────────────────────────────

  // Send to warehouse — opens assignee modal first
  const handleSendToWarehouse = () => {
    setShowSendPrintDropdown(false);
    openAssignModal(countId);
  };

  // Create discrepancy report (for Friday counting)
  const handleCreateDiscrepancyReport = async () => {
    setCreatingDiscrepancyReport(true);
    
    try {
      // Save current product data with discrepancies
      await saveProgress();
      
      // Prepare discrepancy data
      const discrepancyData = products
        .filter(p => p.physical_count !== null && Math.abs(p.discrepancy || 0) > 0.01)
        .map(p => ({
          product_id: p.id,
          product_code: p.code,
          product_name: p.name,
          expected_qty: p.on_hand_quantity ?? p.on_hand_balance ?? p.free_stock,
          physical_count: p.physical_count,
          discrepancy: p.discrepancy
        }));
      
      await api.post(`${API}/pi-counts/${countId}/create-discrepancy-report`);
      toast.success('Discrepancy report created for Friday counting');
      setShowDiscrepancyModal(false);
      navigate('/pi-counts');
    } catch (error) {
      console.error('Error creating discrepancy report:', error);
      toast.error(error.response?.data?.detail || 'Failed to create discrepancy report');
    } finally {
      setCreatingDiscrepancyReport(false);
    }
  };

  // Create a second count - directly creates a new count with only discrepancy products
  const handleCreateSecondCount = async () => {
    setCreatingSecondCount(true);
    
    try {
      // Save progress first
      await saveProgress();
      
      // Create second count directly (NOT via discrepancy report)
      const countRes = await api.post(`${API}/pi-counts/${countId}/create-second-count`);
      toast.success('Second count created - ready to count discrepancy products');
      setShowDiscrepancyModal(false);
      navigate(`/pi-counts/${countRes.data.count.id}`);
    } catch (error) {
      console.error('Error creating second count:', error);
      toast.error(error.response?.data?.detail || 'Failed to create second count');
    } finally {
      setCreatingSecondCount(false);
    }
  };

  // Delete count(s) — selective across the linked chain. The new modal
  // lets the user tick which counts (Original / 2nd / 3rd …) to remove,
  // and for combined counts which suppliers within each count to drop.
  // Selections shape: { [count_id]: { all: bool, supplier_ids: Set<string> } }
  const toggleDeleteWholeCount = (cId, linkedDoc) => {
    setDeleteSelections(prev => {
      const next = { ...prev };
      const cur = next[cId] || { all: false, supplier_ids: new Set() };
      if (cur.all) {
        next[cId] = { all: false, supplier_ids: new Set() };
      } else {
        // Tick whole count → also tick every supplier checkbox visually.
        const allIds = new Set((linkedDoc?.supplier_groups || []).map(sg => sg.supplier_group_id));
        next[cId] = { all: true, supplier_ids: allIds };
      }
      return next;
    });
  };

  const toggleDeleteSupplier = (cId, sgId, totalSuppliers) => {
    setDeleteSelections(prev => {
      const next = { ...prev };
      const cur = next[cId] || { all: false, supplier_ids: new Set() };
      const ids = new Set(cur.supplier_ids);
      if (ids.has(sgId)) ids.delete(sgId); else ids.add(sgId);
      next[cId] = {
        all: ids.size > 0 && ids.size === totalSuppliers,
        supplier_ids: ids,
      };
      return next;
    });
  };

  const handleDeleteCount = async () => {
    // Build payload from selections.
    const selections = [];
    Object.entries(deleteSelections).forEach(([cId, sel]) => {
      if (sel.all) {
        selections.push({ count_id: cId, supplier_group_ids: ['all'] });
      } else if (sel.supplier_ids && sel.supplier_ids.size > 0) {
        selections.push({ count_id: cId, supplier_group_ids: Array.from(sel.supplier_ids) });
      }
    });

    if (selections.length === 0) {
      toast.error('Tick at least one count or supplier to delete');
      return;
    }

    setDeleting(true);
    try {
      const res = await api.post(`${API}/pi-counts/delete-selective`, { selections });
      const deleted = res.data?.deleted_count_ids || [];
      const modified = res.data?.modified_count_ids || [];
      const parts = [];
      if (deleted.length) parts.push(`${deleted.length} count${deleted.length === 1 ? '' : 's'} removed`);
      if (modified.length) parts.push(`${modified.length} count${modified.length === 1 ? '' : 's'} updated`);
      toast.success(parts.join(' · ') || 'Selective delete complete');
      setShowDeleteConfirm(false);
      setDeleteSelections({});

      // If the count we're currently viewing got fully deleted, navigate
      // back to the list. Otherwise reload its details.
      if (deleted.includes(countId)) {
        navigate('/pi-counts');
      } else {
        loadCountDetails();
      }
    } catch (error) {
      console.error('Error deleting count:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete count');
    } finally {
      setDeleting(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <AppHeader user={user} subHeaderLeft={subHeaderLeft} />
      
      <div className="px-4 lg:px-8 py-6" style={{ paddingTop: '164px' }}>
        <div className="max-w-[1400px] mx-auto">
        {/* Linked-count navigation pills + related-artifact jump buttons.
            Pills only render when this count is part of a chain (original +
            one or more recounts). The right-side Adjusted / Rescheduled
            buttons render whenever this chain has produced an adjustment
            report or a scheduled bucket. */}
        {/* Linked-count navigation pills + related-artifact jump buttons.
            Always renders the Original Count pill so the user can see the
            count chain at a glance — even when there's only one count. */}
        {(() => {
          // Build the pill list. If `linkedCounts` came back empty (solo
          // count), fall back to a single synthetic "Original Count" pill
          // for the current count itself.
          const pillList = (linkedCounts && linkedCounts.length > 0)
            ? linkedCounts
            : [{
                id: countId,
                recount_number: 0,
                status: count?.status,
              }];
          return (
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3" data-testid="linked-counts-nav">
            <div className="flex flex-wrap items-center gap-2">
              {pillList.map((lc) => {
                const rn = lc.recount_number || 0;
                const label = rn === 0 ? 'Original Count'
                  : rn === 1 ? '2nd Count'
                  : rn === 2 ? '3rd Count'
                  : `${rn + 1}th Count`;
                const isActive = lc.id === countId;
                const highestRn = pillList.reduce((acc, x) => Math.max(acc, x.recount_number || 0), 0);
                const isSuperseded = lc.status === 'completed' && rn < highestRn;
                const pillColor = isSuperseded
                  ? '#16a34a'                                  // green-600
                  : (rn === 0 ? '#ee7203' /* orange */ : '#3b82f6' /* blue-500 */);
                return (
                  <button
                    key={lc.id}
                    onClick={() => { if (!isActive) navigate(`/pi-counts/${lc.id}`); }}
                    disabled={isActive}
                    className="px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider transition-all border-2 hover:shadow-sm"
                    style={isActive
                      ? { backgroundColor: pillColor, borderColor: pillColor, color: '#fff', cursor: 'default' }
                      : { backgroundColor: '#fff', borderColor: pillColor, color: pillColor }
                    }
                    data-testid={`linked-count-pill-${lc.id}`}
                    title={label}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {relatedAdjustments.length > 0 && (() => {
                const color = '#0d9488';
                return (
                  <button
                    onClick={() => navigate(`/pi-counts/adjustment/${relatedAdjustments[0].id}`)}
                    className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider transition-all border-2 hover:shadow-sm"
                    style={{ backgroundColor: '#fff', borderColor: color, color }}
                    data-testid="related-adjustment-btn"
                    title={`Open the adjustment report created from this count${relatedAdjustments.length > 1 ? ` (latest of ${relatedAdjustments.length})` : ''}`}
                  >
                    <ClipboardList className="w-3.5 h-3.5" />
                    Adjusted{relatedAdjustments.length > 1 ? ` (${relatedAdjustments.length})` : ''}
                  </button>
                );
              })()}
              {relatedBuckets.length > 0 && (() => {
                const color = '#dc2626';
                return (
                  <button
                    onClick={() => navigate(`/pi-counts/${relatedBuckets[0].id}`)}
                    className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider transition-all border-2 hover:shadow-sm"
                    style={{ backgroundColor: '#fff', borderColor: color, color }}
                    data-testid="related-bucket-btn"
                    title={`Open the scheduled count created from this count${relatedBuckets.length > 1 ? ` (latest of ${relatedBuckets.length})` : ''}`}
                  >
                    <FileText className="w-3.5 h-3.5" />
                    Rescheduled{relatedBuckets.length > 1 ? ` (${relatedBuckets.length})` : ''}
                  </button>
                );
              })()}
            </div>
          </div>
          );
        })()}

        {/* Count Header */}
        <Card className="mb-6 bg-white">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <CardTitle className="text-2xl" style={{ color: enconOrange }}>
                  {count.supplier_name}
                </CardTitle>
                <p className="text-sm text-gray-600 mt-1">
                  {safeFormatDate(count.date, 'EEEE, dd MMMM yyyy')}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Category: <span className={count.type === 'structural' ? 'text-blue-600' : 'text-green-600'}>
                    {count.type}
                  </span>
                </p>

                {count.counted_by_name && (
                  <div className="inline-flex items-center gap-1.5 mt-2 px-2.5 py-1 rounded-full bg-green-50 border border-green-200" data-testid="counted-by-label">
                    <svg className="w-3.5 h-3.5 text-green-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                    <span className="text-xs font-semibold text-green-700">Counted by: {count.counted_by_name}</span>
                  </div>
                )}

                {/* Status narrative — surfaces what's currently happening to
                    this count in plain English. Renders one or more lines:
                      • "This count is completed" (green) when status=completed
                      • "N items rescheduled to be counted on Fri 8th" (red,
                        clickable, jumps to the scheduled bucket) when this
                        chain spawned a scheduled bucket
                      • "N items moved to adjustment" (teal, clickable) when
                        this chain spawned an adjustment report */}
                {(() => {
                  const lines = [];
                  if (count.status === 'completed') {
                    lines.push(
                      <p key="completed" className="text-xs font-semibold mt-2" style={{ color: '#16a34a' }} data-testid="status-narrative-completed">
                        This count is completed.
                      </p>
                    );
                  }
                  if (relatedBuckets.length > 0) {
                    const b = relatedBuckets[0];
                    const itemCount = (b.linked_item_codes && b.linked_item_codes.length) || (b.product_counts && b.product_counts.length) || 0;
                    const friendly = b.date ? safeFormatDate(b.date, 'EEEE do MMMM') : 'a future date';
                    const more = relatedBuckets.length > 1 ? ` (+${relatedBuckets.length - 1} more)` : '';
                    lines.push(
                      <button
                        key="rescheduled"
                        onClick={() => navigate(`/pi-counts/${b.id}`)}
                        className="text-xs font-semibold mt-2 underline-offset-2 hover:underline text-left block"
                        style={{ color: '#dc2626' }}
                        data-testid="status-narrative-rescheduled"
                        title="Open the scheduled count"
                      >
                        {itemCount} item{itemCount === 1 ? '' : 's'} rescheduled to be counted on {friendly}{more}
                      </button>
                    );
                  }
                  if (relatedAdjustments.length > 0) {
                    const a = relatedAdjustments[0];
                    const itemCount = (a.items && a.items.length) || (a.products && a.products.length) || 0;
                    const more = relatedAdjustments.length > 1 ? ` (+${relatedAdjustments.length - 1} more)` : '';
                    lines.push(
                      <button
                        key="adjusted"
                        onClick={() => navigate(`/pi-counts/adjustment/${a.id}`)}
                        className="text-xs font-semibold mt-2 underline-offset-2 hover:underline text-left block"
                        style={{ color: '#0d9488' }}
                        data-testid="status-narrative-adjusted"
                        title="Open the adjustment report"
                      >
                        {itemCount > 0 ? `${itemCount} item${itemCount === 1 ? '' : 's'} moved to adjustment` : 'An adjustment was created from this count'}{more}
                      </button>
                    );
                  }
                  return lines.length > 0 ? <div className="space-y-0">{lines}</div> : null;
                })()}
              </div>
              <div className="text-right">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    count.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {count.status}
                  </span>
                </div>
                <p className="text-sm text-gray-600">
                  {completedCount} of {products.length} counted
                </p>
                {totalDiscrepancies > 0 && (
                  <p className="text-sm text-red-600 mt-1">
                    {totalDiscrepancies} discrepancies
                  </p>
                )}
              </div>
            </div>
          </CardHeader>
        </Card>
        
        {/* Second Count Info Banner removed — navigation pills above now
            expose the chain, so this verbose info block is no longer needed. */}

        {/* Action Buttons */}
        <div className="flex items-center justify-between mb-6 gap-3">
          <div className="flex gap-3 flex-wrap items-center">
            <Button
              onClick={handleCompleteCount}
              disabled={completing || count?.status === 'completed'}
              className="rounded-full text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
              data-testid="complete-count-btn"
              title="Mark this count complete and close the linked chain"
            >
              <CheckCircle className={`w-4 h-4 mr-2 ${completing ? 'animate-pulse' : ''}`} />
              {completing ? 'Completing…' : (count?.status === 'completed' ? 'Completed' : 'Complete')}
            </Button>

            <Button
              onClick={() => {
                if (selectedProducts().length === 0) { toast.error('Tick at least one row first'); return; }
                setRecountActionOpen(true);
              }}
              disabled={runningRecount || selectedProducts().length === 0}
              className="rounded-full text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              data-testid="start-next-recount-btn"
              title="Create a new count for the ticked rows — choose to count now or send to warehouse"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              {runningRecount ? 'Creating…' : `Create ${nextRecountLabel} (${selectedProducts().length})`}
            </Button>

            <Button
              onClick={() => setScheduleOpen(true)}
              disabled={selectedProducts().length === 0}
              className="rounded-full text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
              data-testid="schedule-count-btn"
              title="Schedule the ticked rows for a future day"
            >
              <FileText className="w-4 h-4 mr-2" />
              Schedule Count ({selectedProducts().length})
            </Button>

            <Button
              onClick={handleAdjustments}
              disabled={runningAdjustments || selectedProducts().length === 0}
              className="rounded-full text-white disabled:opacity-50 border-0"
              style={{ backgroundImage: 'linear-gradient(90deg, #2563eb 0%, #22c55e 100%)' }}
              data-testid="adjustments-btn"
              title="Send the ticked rows into the adjustment flow"
            >
              <AlertCircle className="w-4 h-4 mr-2" />
              {runningAdjustments ? 'Creating…' : `Adjustments (${selectedProducts().length})`}
            </Button>

            {/* Send/Print Dropdown */}
            <div className="relative">
              <Button
                onClick={() => setShowSendPrintDropdown(!showSendPrintDropdown)}
                variant="outline"
                className="rounded-full"
                style={{ borderColor: enconOrange, color: enconOrange }}
              >
                <Send className="w-4 h-4 mr-2" />
                Send / Print
                <ChevronDown className="w-4 h-4 ml-2" />
              </Button>

              {showSendPrintDropdown && (
                <div className="absolute top-full left-0 mt-1 bg-white border-2 rounded-lg shadow-lg z-50 min-w-[180px]" style={{ borderColor: enconOrange }}>
                  <button
                    onClick={handlePrint}
                    className="w-full px-4 py-3 text-left hover:bg-orange-50 flex items-center gap-2 text-sm"
                  >
                    <Printer className="w-4 h-4" style={{ color: enconOrange }} />
                    Print Count Sheet
                  </button>
                  <button
                    onClick={handleSendToWarehouse}
                    disabled={sendingToWarehouse}
                    className="w-full px-4 py-3 text-left hover:bg-orange-50 flex items-center gap-2 text-sm border-t"
                  >
                    <Send className="w-4 h-4" style={{ color: enconOrange }} />
                    {sendingToWarehouse ? 'Sending...' : 'Send to W/H'}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Right-side icon-only actions: M4 refresh + Save progress + Remove from system */}
          <div className="flex items-center gap-2 shrink-0">
            {autoSaveStatus !== 'idle' && (
              <span
                className={`text-xs font-semibold px-2 py-1 rounded-full transition-opacity ${
                  autoSaveStatus === 'saving' ? 'bg-blue-50 text-blue-700' : 'bg-green-50 text-green-700'
                }`}
                data-testid="auto-save-indicator"
                title={autoSaveStatus === 'saving' ? 'Auto-saving…' : 'Changes auto-saved'}
              >
                {autoSaveStatus === 'saving' ? 'Saving…' : 'Auto-saved'}
              </span>
            )}
            <button
              type="button"
              onClick={refreshM4Data}
              disabled={refreshingM4}
              title={refreshingM4 ? 'Refreshing M4 data…' : 'Refresh M4 on-hand figures'}
              aria-label="Refresh M4"
              className="inline-flex items-center gap-1.5 h-10 px-3 rounded-full border-2 border-blue-500 text-blue-600 hover:bg-blue-50 disabled:opacity-50 transition-colors"
              data-testid="m4-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${refreshingM4 ? 'animate-spin' : ''}`} />
              <span className="text-sm font-semibold">M4</span>
            </button>
            <button
              type="button"
              onClick={saveProgress}
              disabled={saving}
              title={saving ? 'Saving…' : 'Save progress'}
              aria-label="Save progress"
              className="inline-flex items-center justify-center w-10 h-10 rounded-full border-2 transition-colors disabled:opacity-50 hover:bg-orange-50"
              style={{ borderColor: enconOrange, color: enconOrange }}
              data-testid="save-progress-icon-btn"
            >
              <Save className={`w-5 h-5 ${saving ? 'animate-pulse' : ''}`} />
            </button>
            {user?.role === 'super_admin' && (
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                title="Remove count from system"
                aria-label="Remove count"
                className="inline-flex items-center justify-center w-10 h-10 rounded-full border-2 border-red-500 text-red-600 hover:bg-red-50 transition-colors"
                data-testid="delete-count-icon-btn"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
        
        {/* Combined Count Tabs - Show only for combined counts */}
        {isCombinedCount && supplierGroups.length > 1 && (
          <div className="mb-6">
            <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-3 mb-4">
              <div className="flex items-center gap-2 text-purple-700">
                <Layers className="w-5 h-5" />
                <span className="font-semibold">Combined Count</span>
                <span className="text-sm text-purple-600">- {supplierGroups.length} supplier groups</span>
              </div>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-2">
              {supplierGroups.map((sg) => {
                const progress = getSupplierGroupProgress(sg.supplier_group_id);
                const isActive = activeSupplierTab === sg.supplier_group_id;
                const progressPercent = progress.total > 0 ? Math.round((progress.counted / progress.total) * 100) : 0;
                
                return (
                  <button
                    key={sg.supplier_group_id}
                    onClick={() => setActiveSupplierTab(sg.supplier_group_id)}
                    className={`px-4 py-3 rounded-lg border-2 transition-all min-w-[180px] flex-shrink-0 ${
                      isActive 
                        ? 'bg-orange-50 border-orange-500 shadow-md' 
                        : 'bg-white border-gray-200 hover:border-orange-300'
                    }`}
                  >
                    <div className="text-left">
                      <div className={`font-semibold text-sm truncate ${isActive ? 'text-orange-700' : 'text-gray-700'}`}>
                        {sg.supplier_name}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                          <div 
                            className="h-1.5 rounded-full transition-all"
                            style={{ 
                              width: `${progressPercent}%`,
                              backgroundColor: progressPercent === 100 ? '#22c55e' : enconOrange
                            }}
                          />
                        </div>
                        <span className={`text-xs font-medium ${
                          progressPercent === 100 ? 'text-green-600' : 'text-gray-500'
                        }`}>
                          {progress.counted}/{progress.total}
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
        
        {/* Products Table */}
        <Card className="bg-white">
          <CardContent className="p-0">
            {/* Active Supplier Group Header for Combined Counts */}
            {isCombinedCount && activeSupplierTab && (
              <div className="px-4 py-3 bg-orange-50 border-b-2 flex items-center justify-between" style={{ borderColor: enconOrange }}>
                <div>
                  <span className="font-semibold" style={{ color: enconOrange }}>
                    {supplierGroups.find(sg => sg.supplier_group_id === activeSupplierTab)?.supplier_name}
                  </span>
                  <span className="text-sm text-gray-500 ml-2">
                    ({filteredProducts.length} products)
                  </span>
                </div>
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b-2" style={{ borderColor: enconOrange }}>
                  {/* Top header row — groups + Original toggle header */}
                  <tr>
                    <th rowSpan={2} className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Product Code</th>
                    <th rowSpan={2} className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Description</th>
                    <th rowSpan={2} className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase">{showOriginal && count?.is_second_count ? 'On Hand Qty (Original)' : 'On Hand Qty'}</th>
                    {count?.type === 'shelf_life' && (
                      <th rowSpan={2} className="px-2 py-3 text-center text-xs font-semibold uppercase" style={{ width: '150px', color: '#dc2626' }}>Expiry Date</th>
                    )}
                    {count?.is_second_count && (
                      <th colSpan={2} className="px-2 py-2 text-center bg-yellow-50 border-l border-yellow-200" style={{ width: '180px' }}>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={showOriginal}
                          onClick={handleToggleShowOriginal}
                          className="inline-flex items-center gap-2 select-none cursor-pointer focus:outline-none"
                          data-testid="toggle-show-original"
                          title={showOriginal ? 'Hide original count values' : 'Show original count values'}
                        >
                          <span className="text-[11px] font-bold uppercase tracking-wide text-yellow-800">Show Original</span>
                          <span
                            className={`relative inline-block w-9 h-5 rounded-full transition-colors ${showOriginal ? 'bg-orange-500' : 'bg-gray-300'}`}
                          >
                            <span
                              className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${showOriginal ? 'translate-x-4' : ''}`}
                            />
                          </span>
                        </button>
                      </th>
                    )}
                    <th rowSpan={2} className="px-2 py-3 text-center text-xs font-semibold text-gray-700 uppercase" style={{ width: '90px' }}>Physical Count</th>
                    <th rowSpan={2} className="px-2 py-3 text-center text-xs font-semibold text-gray-700 uppercase" style={{ width: '90px' }}>On the Road</th>
                    <th rowSpan={2} className="px-2 py-3 text-center text-xs font-semibold uppercase" style={{ width: '80px', color: enconOrange }}>Adhoc</th>
                    <th rowSpan={2} className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase">Discrepancy</th>
                    <th rowSpan={2} className="px-2 py-3 text-center text-xs font-semibold uppercase" style={{ width: '60px', color: enconOrange }}>Select</th>
                  </tr>
                  {/* Second header row — sub-labels under the Original toggle (only when on) */}
                  {count?.is_second_count && (
                    <tr>
                      {showOriginal ? (
                        <>
                          <th className="px-2 py-2 text-right text-[11px] font-medium text-yellow-700 uppercase bg-yellow-50 border-l border-yellow-200" style={{ width: '90px' }}>Count</th>
                          <th className="px-2 py-2 text-right text-[11px] font-medium text-yellow-700 uppercase bg-yellow-50" style={{ width: '90px' }}>Disc.</th>
                        </>
                      ) : (
                        <th colSpan={2} className="bg-yellow-50 border-l border-yellow-200" style={{ width: '180px' }}></th>
                      )}
                    </tr>
                  )}
                </thead>
                <tbody>
                  {Array.isArray(filteredProducts) && filteredProducts.map((product, index) => {
                    // Defensive check: ensure product is an object
                    if (!product || typeof product !== 'object') return null;
                    
                    const productId = product.id || `product-${index}`;
                    const productCode = String(product.code || product.product_code || '-');
                    const productName = String(product.name || product.description || product.product_name || '');
                    const originalOnHandQty = originalOnHandFor(product);
                    const onHandQty = originalOnHandQty !== null
                      ? originalOnHandQty
                      : (product.on_hand_quantity ?? product.on_hand_balance ?? product.free_stock ?? 0);
                    const stockUnit = String(product.stock_unit || '');
                    const hasDiscrepancy = product.physical_count !== null && Math.abs(product.discrepancy || 0) > 0.01;
                    
                    return (
                      <React.Fragment key={productId}>
                      <tr 
                        className={`border-b hover:bg-gray-50 ${hasDiscrepancy ? 'bg-red-50' : ''}`}
                      >
                        <td className="px-4 py-3 text-sm font-medium">
                          {productCode}
                          {product.not_in_stock && <span className="text-red-500 font-bold ml-0.5">*</span>}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 max-w-xs" title={productName}>
                          <div className="truncate">
                            {productName || <span className="text-gray-400 italic">No description</span>}
                          </div>
                          {/* Warehouse note (set on the warehouse-side detail page).
                              Displayed read-only under the description so the office
                              team can see what the warehouse flagged about this product
                              for the current count. Resets per recount — see
                              backend `create_second_count_direct` which writes
                              `notes: ""` for every freshly-created recount line. */}
                          {product.notes && String(product.notes).trim() && (
                            <div
                              className="mt-1 text-[11px] italic text-blue-700 leading-snug whitespace-pre-wrap"
                              data-testid={`product-warehouse-note-${productCode}`}
                              title="Warehouse note for this count"
                            >
                              <FileText className="w-3 h-3 inline mr-1 -mt-0.5 text-blue-500" />
                              {String(product.notes).trim()}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-semibold">
                          {typeof onHandQty === 'number' ? onHandQty.toFixed(2) : '0.00'}
                          {stockUnit && <span className="text-xs text-gray-500 ml-1">{stockUnit}</span>}
                        </td>
                        {count?.type === 'shelf_life' && (
                          <td className="px-2 py-3 text-center" style={{ width: '150px' }}>
                            <Input
                              type="date"
                              value={product.expiry_date || ''}
                              onChange={(e) => handleExpiryDateChange(product.id, productCode, e.target.value)}
                              className="text-center text-sm"
                              data-testid={`expiry-date-${productCode}`}
                            />
                          </td>
                        )}
                        {/* Show original count data for second counts */}
                        {count?.is_second_count && (
                          <>
                            {showOriginal ? (
                              <>
                                <td className="px-2 py-3 text-sm text-right font-semibold bg-yellow-50" style={{ width: '90px' }}>
                                  {product.original_physical_count !== null && product.original_physical_count !== undefined 
                                    ? parseFloat(product.original_physical_count).toFixed(2) 
                                    : (product.original_count !== null && product.original_count !== undefined
                                      ? parseFloat(product.original_count).toFixed(2)
                                      : '-')}
                                </td>
                                <td className={`px-2 py-3 text-sm text-right font-semibold bg-yellow-50 ${
                                  (product.original_discrepancy || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                                }`}>
                                  {product.original_discrepancy !== null && product.original_discrepancy !== undefined 
                                    ? `${(product.original_discrepancy || 0) >= 0 ? '+' : ''}${parseFloat(product.original_discrepancy).toFixed(2)}` 
                                    : '-'}
                                </td>
                              </>
                            ) : (
                              <td colSpan={2} className="bg-yellow-50 border-l border-yellow-200" style={{ width: '180px' }}></td>
                            )}
                          </>
                        )}
                        <td className="px-2 py-3" style={{ width: '90px' }}>
                          <Input
                            type="number"
                            step="0.01"
                            value={product.physical_count === null ? '' : product.physical_count}
                            onChange={(e) => handlePhysicalCountChange(product.id, productCode, e.target.value)}
                            placeholder="Count"
                            className="text-center text-sm"
                            style={{ width: '80px' }}
                          />
                        </td>
                        <td className="px-2 py-3" style={{ width: '90px' }}>
                          <Input
                            type="number"
                            step="0.01"
                            value={product.on_the_road || ''}
                            onChange={(e) => handleOnTheRoadChange(product.id, productCode, e.target.value)}
                            placeholder="0"
                            className="text-center text-sm"
                            style={{ width: '80px' }}
                          />
                        </td>
                        <td className="px-2 py-3 text-center" style={{ width: '80px' }}>
                          <button
                            onClick={() => setAdhocModalOpen(productId)}
                            className="px-3 py-2 rounded-lg text-xs font-bold transition-all hover:bg-orange-50"
                            style={{ backgroundColor: 'white', border: `2px solid ${enconOrange}`, color: '#1e293b', minWidth: '50px' }}
                            data-testid={`adhoc-btn-${productId}`}
                            title={product.adhoc_note || 'Adhoc adjustment'}
                          >
                            {product.adhoc_quantity && product.adhoc_quantity !== 0
                              ? `${product.adhoc_quantity > 0 ? '+' : ''}${product.adhoc_quantity}`
                              : 'Adhoc'}
                          </button>
                        </td>
                        <td className={`px-4 py-3 text-sm text-right font-bold ${
                          product.physical_count !== null 
                            ? ((product.discrepancy || 0) >= 0 ? 'text-green-600' : 'text-red-600')
                            : 'text-gray-400'
                        }`}>
                          {product.physical_count !== null 
                            ? `${(product.discrepancy || 0) >= 0 ? '+' : ''}${(product.discrepancy || 0).toFixed(2)}` 
                            : '-'}
                        </td>
                        <td className="px-2 py-3 text-center" style={{ width: '60px' }}>
                          <input
                            type="checkbox"
                            checked={!!selected[productKey(product)]}
                            onChange={() => toggleSelected(productKey(product))}
                            className="w-4 h-4 accent-orange-500"
                            data-testid={`select-product-${productCode}`}
                          />
                        </td>
                      </tr>
                      {/* Adhoc note display row */}
                      {product.adhoc_note && product.adhoc_quantity !== 0 && (
                        <tr className="border-b bg-orange-50/50">
                          {/* Span columns before Adhoc */}
                          <td colSpan={(count?.type === 'shelf_life' ? 1 : 0) + (count?.is_second_count ? 5 : 3)}></td>
                          <td colSpan={count?.is_second_count ? 5 : 5} className="px-2 py-2">
                            <div className="text-xs break-words">
                              <span className="font-bold" style={{ color: enconOrange }}>AdHoc Note - </span>
                              <span className="text-gray-600">{product.adhoc_note}</span>
                            </div>
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
      </div>

      {/* Schedule Count Modal — pick a future day for the ticked items */}
      <Dialog open={scheduleOpen} onOpenChange={setScheduleOpen}>
        <DialogContent
          className="max-w-md rounded-2xl"
          style={{ background: 'white', borderRadius: '16px', border: `3px solid ${enconOrange}` }}
        >
          <DialogHeader>
            <DialogTitle className="text-lg font-bold" style={{ color: enconOrange }}>
              Schedule Count
            </DialogTitle>
            <DialogDescription>
              Choose a future date to schedule the selected items as a linked count. They'll appear on the Actions table for that day.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              {selectedProducts().length} item{selectedProducts().length === 1 ? '' : 's'} will be scheduled as a linked count for the date you pick. They'll appear on that day's Actions table.
            </p>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Scheduled date</label>
              <Input
                type="date"
                value={scheduleDate}
                onChange={(e) => setScheduleDate(e.target.value)}
                data-testid="schedule-date-input"
                min={new Date().toISOString().slice(0, 10)}
              />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setScheduleOpen(false)}>Cancel</Button>
              <Button
                onClick={handleScheduleSubmit}
                disabled={scheduling || selectedProducts().length === 0}
                style={{ backgroundColor: enconOrange, color: 'white' }}
                data-testid="schedule-confirm-btn"
              >
                {scheduling ? 'Scheduling…' : 'Schedule'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Recount action modal — pick between counting it now or sending it
          to the warehouse queue. Title/description omitted per user request:
          the two button labels are self-explanatory. */}
      <Dialog open={recountActionOpen} onOpenChange={setRecountActionOpen}>
        <DialogContent
          className="max-w-md rounded-2xl"
          style={{ background: 'white', borderRadius: '16px', border: `3px solid ${enconOrange}` }}
          data-testid="recount-action-modal"
        >
          <DialogHeader className="sr-only">
            <DialogTitle>{nextRecountLabel}</DialogTitle>
            <DialogDescription>{selectedProducts().length} item{selectedProducts().length === 1 ? '' : 's'} selected</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              onClick={() => handleStartNextRecount('count-first')}
              disabled={runningRecount}
              className="flex flex-col items-center justify-center gap-2 p-4 rounded-2xl border-2 border-blue-500 text-blue-600 transition-all disabled:opacity-50 hover:shadow-md"
              data-testid="recount-count-first-btn"
            >
              <FileCheck className="w-8 h-8" />
              <span className="font-semibold">Open {nextRecountLabel}</span>
            </button>
            <button
              onClick={() => handleStartNextRecount('send-to-wh')}
              disabled={runningRecount}
              className="flex flex-col items-center justify-center gap-2 p-4 rounded-2xl border-2 border-blue-500 text-blue-600 transition-all disabled:opacity-50 hover:shadow-md"
              data-testid="recount-send-wh-btn"
            >
              <Send className="w-8 h-8" />
              <span className="font-semibold">Send {nextRecountLabel} to W/H</span>
            </button>
          </div>
          {runningRecount && (
            <p className="text-xs text-gray-500 text-center mt-2">Working…</p>
          )}
        </DialogContent>
      </Dialog>


      

      {/* Selective Delete Modal (Super Admin Only)
          Lists every count in the chain. Tick a whole count to remove it
          entirely, OR expand a combined count and tick individual suppliers
          to strip just those supplier groups from that count. Supports
          deleting multiple counts at once. */}
      <Dialog
        open={showDeleteConfirm}
        onOpenChange={(o) => { setShowDeleteConfirm(o); if (!o) setDeleteSelections({}); }}
      >
        <DialogContent
          className="max-w-2xl rounded-2xl"
          style={{ background: 'white', borderRadius: '16px', border: '3px solid #ef4444' }}
          data-testid="selective-delete-modal"
        >
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-red-600 flex items-center gap-2">
              <Trash2 className="w-6 h-6" />
              Remove Counts from System
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-600">
              Tick the count(s) you want to delete. For combined counts you can
              also pick individual suppliers to remove from that count only.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-1">
            {((linkedCounts && linkedCounts.length > 0)
              ? linkedCounts
              : [{ id: countId, recount_number: 0, status: count?.status, date: count?.date, supplier_name: count?.supplier_name, is_combined: count?.is_combined, supplier_groups: (count?.supplier_groups || []).map(sg => ({ supplier_group_id: sg.supplier_group_id, supplier_name: sg.supplier_name, total: sg.total || (sg.product_counts?.length || 0) })) }]
            ).map((lc) => {
              const cId = lc.id;
              const cur = deleteSelections[cId] || { all: false, supplier_ids: new Set() };
              const sgs = lc.supplier_groups || [];
              const isCombined = lc.is_combined && sgs.length > 1;
              const recountLabel = lc.recount_number === 0
                ? 'Original Count'
                : `${lc.recount_number === 1 ? '2nd' : lc.recount_number === 2 ? '3rd' : `${lc.recount_number + 1}th`} Count`;
              const isCurrent = cId === countId;

              return (
                <div
                  key={cId}
                  className={`border-2 rounded-xl p-3 transition-colors ${cur.all ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-white'}`}
                  data-testid={`delete-row-count-${cId}`}
                >
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={cur.all}
                      onChange={() => toggleDeleteWholeCount(cId, lc)}
                      className="w-5 h-5 mt-0.5 accent-red-600 cursor-pointer"
                      data-testid={`delete-whole-checkbox-${cId}`}
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-gray-900">{recountLabel}</span>
                        {isCurrent && (
                          <span className="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-full bg-orange-100 text-orange-700 border border-orange-300">
                            Currently viewing
                          </span>
                        )}
                        {lc.status && (
                          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-gray-200">
                            {lc.status.replace(/_/g, ' ')}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mt-0.5">
                        {lc.supplier_name || 'Unknown supplier'}
                        {lc.date && <span className="text-gray-400"> · {safeFormatDate(lc.date, 'dd MMM yyyy')}</span>}
                      </p>
                    </div>
                  </label>

                  {isCombined && (
                    <div className="mt-2 ml-8 pl-3 border-l-2 border-gray-200 space-y-1.5">
                      <p className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">
                        Or pick suppliers to delete only from this count
                      </p>
                      {sgs.map(sg => {
                        const ticked = cur.supplier_ids.has(sg.supplier_group_id);
                        return (
                          <label
                            key={sg.supplier_group_id}
                            className="flex items-center gap-2 cursor-pointer text-sm py-1"
                            data-testid={`delete-supplier-row-${cId}-${sg.supplier_group_id}`}
                          >
                            <input
                              type="checkbox"
                              checked={ticked}
                              onChange={() => toggleDeleteSupplier(cId, sg.supplier_group_id, sgs.length)}
                              className="w-4 h-4 accent-red-600 cursor-pointer"
                              data-testid={`delete-supplier-checkbox-${cId}-${sg.supplier_group_id}`}
                            />
                            <span className="text-gray-700">{sg.supplier_name || 'Unknown'}</span>
                            {typeof sg.total === 'number' && (
                              <span className="text-xs text-gray-400">({sg.total} item{sg.total === 1 ? '' : 's'})</span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <p className="text-xs text-gray-500 mt-1">
            This action cannot be undone. Selected data is permanently deleted.
          </p>

          <div className="flex gap-3 pt-2">
            <Button
              onClick={() => { setShowDeleteConfirm(false); setDeleteSelections({}); }}
              variant="outline"
              className="flex-1"
              data-testid="selective-delete-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteCount}
              disabled={deleting}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              data-testid="selective-delete-confirm-btn"
            >
              {deleting ? 'Removing…' : 'Remove Selected'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Adhoc Adjustment Modal */}
      <AdhocModal
        isOpen={adhocModalOpen !== null}
        onClose={() => setAdhocModalOpen(null)}
        onSave={(qty, note) => {
          const product = products.find(p => p.id === adhocModalOpen);
          if (product) {
            handleAdhocSave(product.id, product.code || product.product_code, qty, note);
          }
        }}
        productName={(() => {
          const product = products.find(p => p.id === adhocModalOpen);
          return product ? (product.name || product.description || product.product_name || product.code || '') : '';
        })()}
        currentQty={(() => {
          const product = products.find(p => p.id === adhocModalOpen);
          return product ? (product.adhoc_quantity || 0) : 0;
        })()}
        currentNote={(() => {
          const product = products.find(p => p.id === adhocModalOpen);
          return product ? (product.adhoc_note || '') : '';
        })()}
      />

      {/* ── Assignee selection modal (shown before any Send to W/H action) ── */}
      <Dialog open={showAssignModal} onOpenChange={(o) => { if (!o) setShowAssignModal(false); }}>
        <DialogContent className="max-w-lg rounded-2xl" style={{ fontFamily: 'Inter, sans-serif' }}>
          <DialogHeader>
            <DialogTitle className="text-lg font-extrabold text-gray-900">Assign Count</DialogTitle>
            <DialogDescription className="text-sm text-gray-500">
              Choose who should complete this count in the warehouse.
            </DialogDescription>
          </DialogHeader>

          {/* Team filter tabs */}
          <div className="flex flex-wrap gap-1.5 mt-1">
            {[
              { key: 'default', label: 'Warehouse / Ops' },
              { key: 'all', label: 'All Staff' },
              { key: 'office_sales', label: 'Office / Sales' },
              { key: 'admin', label: 'Admin' },
            ].map(tab => (
              <button key={tab.key} onClick={() => setWhTeamFilter(tab.key)}
                className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${whTeamFilter === tab.key ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-gray-600 border-gray-300 hover:border-orange-400'}`}>
                {tab.label}
              </button>
            ))}
          </div>

          {/* Team member list */}
          <div className="max-h-56 overflow-y-auto border border-gray-200 rounded-xl divide-y">
            {getWhFilteredMembers().length === 0 ? (
              <div className="p-4 text-sm text-gray-500 text-center">No team members found.</div>
            ) : getWhFilteredMembers().map((m, idx) => (
              <button key={m.id || idx}
                onClick={() => { setWhAssigneeName(m.name || `${m.first_name || ''} ${m.last_name || ''}`.trim()); setWhAssigneeEmail(m.email || ''); }}
                className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors ${whAssigneeName === (m.name || `${m.first_name || ''} ${m.last_name || ''}`.trim()) ? 'bg-orange-50 border-l-4 border-orange-500' : 'hover:bg-gray-50'}`}>
                <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center text-sm font-bold text-orange-700 flex-shrink-0">
                  {(m.name || m.first_name || '?')[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-gray-900 truncate">{m.name || `${m.first_name || ''} ${m.last_name || ''}`.trim()}</div>
                  {m.email && <div className="text-xs text-gray-500 truncate">{m.email}</div>}
                </div>
                {whAssigneeName === (m.name || `${m.first_name || ''} ${m.last_name || ''}`.trim()) && (
                  <Check className="w-4 h-4 text-orange-500 flex-shrink-0" />
                )}
              </button>
            ))}
          </div>

          {/* Manual name input */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1">Or enter a name manually</label>
            <input
              type="text"
              value={whAssigneeName}
              onChange={e => setWhAssigneeName(e.target.value)}
              placeholder="Name of person completing the count"
              className="w-full border-2 border-gray-300 rounded-xl px-3 py-2 text-sm focus:border-orange-400 focus:ring-2 focus:ring-orange-200 focus:outline-none"
              data-testid="wh-assignee-name-input"
            />
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={() => setShowAssignModal(false)} className="rounded-xl h-10 px-4">Cancel</Button>
            <Button
              onClick={confirmSendWithAssignee}
              disabled={sendingToWarehouse}
              className="rounded-xl h-10 px-5 text-white font-semibold"
              style={{ backgroundColor: '#ee7203' }}
              data-testid="wh-confirm-send-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              {whAssigneeName.trim() ? `Send to ${whAssigneeName.trim().split(' ')[0]}` : 'Send to W/H'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default PICountDetail;
